/**
 * Faktury z KSeF - faktury zakupu pobrane z Krajowego Systemu e-Faktur jako nabywca (tylko panel admina).
 * Kontrakt = backend/api/ksef_otrzymane.py (zrodlo prawdy).
 *
 * Dane: GET /ksef/otrzymane/podsumowanie (liczby per status za caly czas, per miesiac, sprzedawcy, ostatnia synchronizacja,
 * sync_w_toku_id, ustawienia{pobieranie_zakupow, ma_token, od_domyslne}) + GET /ksef/otrzymane (tabela; status/rodzaj/nip/
 * zakres dat wystawienia/szukaj/strona; domyslnie 90 dni). Sklepy do modalu dodawania z GET /locations/ (szczegoly maja wlasne).
 * "Pobierz z KSeF" = POST /sync {tryb:'biezace'} -> 202 {sync_id} i odpytywanie GET /sync/status/<id> co 3 s do ok/blad
 * (jak w Zamowieniach z B2B). "Pobierz od…" = tryb 'pelna' z data od (domyslnie ustawienia.od_domyslne).
 * "Dopasuj ponownie" = POST /<id>/dopasuj dla wierszy z biezacej strony w statusie nowa/dopasowana/blad (po kolei, z licznikiem).
 * Podglad = modal szczegolow (pozycje z dopasowaniem, korekta); "Dodaj" = modal z wyborem sklepu.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ksefOtrzymaneService } from '../services/ksefOtrzymaneService';
import { locationsService } from '../services/locationsService';
import {
  PodsumowanieKsef, PerMiesiacKsef, TopSprzedawcy, FiltryKsef, TabelaKsef, SzczegolyKsefModal,
  domyslnyZakres, formatujDateCzas, komunikatBledu, dzisISO, OD_CALEJ_HISTORII,
} from '../components/ksef-otrzymane';

const NA_STRONE = 100;
export const ODPYTUJ_CO_MS = 3000;
const MAX_ODPYTAN = 600;          // 30 min
const MAX_BLEDOW_STATUSU = 5;
const STATUSY_DO_DOPASOWANIA = new Set(['nowa', 'dopasowana', 'blad']);

const btnPrimary = { padding: '6px 12px', fontSize: '12px', fontWeight: 600, borderRadius: '4px', cursor: 'pointer', border: '1px solid var(--primary)', background: 'var(--primary)', color: '#fff' };
const btnSecondary = { padding: '6px 12px', fontSize: '12px', fontWeight: 600, borderRadius: '4px', cursor: 'pointer', border: '1px solid var(--border)', background: 'var(--card-bg)', color: 'var(--text)' };
const pole = { padding: '4px 8px', fontSize: '11px', borderRadius: '4px', border: '1px solid var(--border)', backgroundColor: 'var(--card-bg)', color: 'var(--text)' };

/** Pasek postepu synchronizacji (stan z GET /sync/status co 3 s: pobrano, nowych, xml_pobrane, xml_bledy, dopasowane). */
const Postep = ({ postep }) => {
  if (!postep) return null;
  const od = postep.od_daty || postep.od;
  return (
    <div data-testid="postep-sync" style={{ marginTop: '8px', padding: '6px 10px', borderRadius: '4px', fontSize: '11px', backgroundColor: 'var(--info-light)', color: 'var(--info)' }}>
      {postep.tryb === 'pelna' ? `Pobieram faktury z KSeF od ${od || '…'}` : 'Pobieram nowe faktury z KSeF'}
      {' · '}pobrano: {postep.pobrano ?? 0} · nowych: {postep.nowych ?? 0}
      {' · '}XML: {postep.xml_pobrane ?? 0}{postep.xml_bledy ? ` (błędy ${postep.xml_bledy})` : ''}
      {postep.komunikat ? ` · ${postep.komunikat}` : ''}
    </div>
  );
};

const opisWyniku = (st) => {
  const czesci = [`pobrano ${st?.pobrano ?? 0} faktur`, `nowych ${st?.nowych ?? 0}`, `XML ${st?.xml_pobrane ?? 0}`];
  if (st?.xml_bledy) czesci.push(`błędy XML ${st.xml_bledy}`);
  if (st?.dopasowane) czesci.push(`dopasowano ${st.dopasowane}`);
  let tekst = `Pobieranie z KSeF zakończone: ${czesci.join(', ')}`;
  if (st?.komunikat) tekst += `. ${st.komunikat}`;
  return tekst;
};

const KsefOtrzymanePage = () => {
  const [zakres, setZakres] = useState(domyslnyZakres);
  const [status, setStatus] = useState(null);       // null = wszystkie
  const [rodzaj, setRodzaj] = useState(null);       // null = wszystkie
  const [nip, setNip] = useState('');
  const [nipOdlozony, setNipOdlozony] = useState('');
  const [szukaj, setSzukaj] = useState('');
  const [szukajOdlozony, setSzukajOdlozony] = useState('');
  const [strona, setStrona] = useState(1);

  const [sklepy, setSklepy] = useState([]);
  const [podsumowanie, setPodsumowanie] = useState(null);
  const [lista, setLista] = useState({ wiersze: [], razem: 0, strona: 1, stron: 1 });
  const [ladowaniePods, setLadowaniePods] = useState(true);
  const [ladowanieListy, setLadowanieListy] = useState(true);
  const [blad, setBlad] = useState(null);
  const [uruchamianie, setUruchamianie] = useState(false);
  const [syncId, setSyncId] = useState(null);
  const [postep, setPostep] = useState(null);
  const [wynik, setWynik] = useState(null);         // {status: 'ok'|'blad', komunikat}
  const [pobierzOd, setPobierzOd] = useState('');
  const [dopasowywanie, setDopasowywanie] = useState(null);   // {zrobiono, razem}
  const [podglad, setPodglad] = useState(null);
  const listaReq = useRef(0);

  useEffect(() => {
    const t = setTimeout(() => setSzukajOdlozony(szukaj.trim()), 300);
    return () => clearTimeout(t);
  }, [szukaj]);
  useEffect(() => {
    const t = setTimeout(() => setNipOdlozony(nip.trim()), 300);
    return () => clearTimeout(t);
  }, [nip]);

  // Sklepy do modalu dodawania z tabeli (odpowiedz moze byc tablica albo {data: [...]})
  useEffect(() => {
    let aktywny = true;
    locationsService.getLocations()
      .then((d) => {
        if (!aktywny) return;
        const tab = Array.isArray(d) ? d : (Array.isArray(d?.data) ? d.data : []);
        setSklepy(tab.map(l => ({ id: l.id, nazwa: l.nazwa || l.name || `Sklep ${l.id}` })));
      })
      .catch(() => { /* modal dodawania i tak dolozy sklep z dopasowania */ });
    return () => { aktywny = false; };
  }, []);

  const ladujPodsumowanie = useCallback(async () => {
    setLadowaniePods(true);
    try {
      const dane = await ksefOtrzymaneService.podsumowanie();
      setPodsumowanie(dane || null);
      setBlad(null);
      if (dane?.sync_w_toku_id) setSyncId(prev => prev || dane.sync_w_toku_id);
      if (dane?.ustawienia?.od_domyslne) setPobierzOd(prev => prev || dane.ustawienia.od_domyslne);
    } catch (err) {
      if (!err?.isAuthError) setBlad(komunikatBledu(err, 'Nie udało się pobrać podsumowania'));
    } finally {
      setLadowaniePods(false);
    }
  }, []);

  const ladujListe = useCallback(async () => {
    const nr = ++listaReq.current;
    setLadowanieListy(true);
    try {
      const params = { od: zakres.od, do: zakres.do, szukaj: szukajOdlozony, nip: nipOdlozony, strona, na_strone: NA_STRONE };
      if (status) params.status = status;
      if (rodzaj) params.rodzaj = rodzaj;
      const dane = await ksefOtrzymaneService.lista(params);
      if (nr !== listaReq.current) return;
      setLista({
        wiersze: Array.isArray(dane?.wiersze) ? dane.wiersze : [],
        razem: dane?.razem ?? 0,
        strona: dane?.strona ?? strona,
        stron: dane?.stron ?? Math.max(1, Math.ceil((dane?.razem || 0) / NA_STRONE)),
      });
    } catch (err) {
      if (nr !== listaReq.current) return;
      if (!err?.isAuthError) setBlad(komunikatBledu(err, 'Nie udało się pobrać listy faktur'));
    } finally {
      if (nr === listaReq.current) setLadowanieListy(false);
    }
  }, [zakres.od, zakres.do, szukajOdlozony, nipOdlozony, strona, status, rodzaj]);

  useEffect(() => { ladujPodsumowanie(); }, [ladujPodsumowanie]);
  useEffect(() => { ladujListe(); }, [ladujListe]);

  const przeladuj = useCallback(() => { ladujPodsumowanie(); ladujListe(); }, [ladujPodsumowanie, ladujListe]);
  const zmienStatus = (s) => { setStatus(s); setStrona(1); };
  const zmienRodzaj = (r) => { setRodzaj(r); setStrona(1); };
  const zmienNip = (v) => { setNip(v); setStrona(1); };
  const zmienSzukaj = (v) => { setSzukaj(v); setStrona(1); };
  const zmienZakres = (z) => { setZakres(z); setStrona(1); };
  // Klik w sprzedawce: filtr NIP (backend filtruje po cyfrach NIP)
  const wybierzSprzedawce = (nipSprzedawcy) => { setNip(nipSprzedawcy || ''); setStrona(1); };

  const uruchomSync = async (tryb, od = null) => {
    setUruchamianie(true);
    setWynik(null);
    try {
      const odp = await ksefOtrzymaneService.sync(tryb, od);
      if (odp?.sync_id) {
        setPostep({ tryb, od_daty: odp.od || od, pobrano: 0, nowych: 0, xml_pobrane: 0, xml_bledy: 0 });
        setSyncId(odp.sync_id);
      } else {
        setWynik({ status: 'blad', komunikat: 'Backend nie zwrócił identyfikatora synchronizacji' });
      }
    } catch (err) {
      if (!err?.isAuthError) {
        const komunikat = komunikatBledu(err, 'Nie udało się uruchomić pobierania z KSeF.');
        setWynik({ status: 'blad', komunikat });
        toast.error(komunikat);
      }
    } finally {
      setUruchamianie(false);
    }
  };

  const pobierz = () => uruchomSync('biezace');
  const pobierzOdDaty = () => {
    if (!pobierzOd) { toast.error('Podaj datę, od której pobrać faktury z KSeF.'); return; }
    const ok = window.confirm(`Pobrać z KSeF wszystkie faktury zakupu od ${pobierzOd}? To może potrwać kilka minut — działa w tle.`);
    if (ok) uruchomSync('pelna', pobierzOd);
  };

  /** "Dopasuj ponownie": POST /<id>/dopasuj po kolei dla wierszy z biezacej strony (nowe / dopasowane / bledy). */
  const dopasujPonownie = async () => {
    const doDopasowania = lista.wiersze.filter(w => STATUSY_DO_DOPASOWANIA.has(w.status));
    if (doDopasowania.length === 0) { toast('Na tej stronie nie ma faktur do dopasowania (nowe, dopasowane, błędy).'); return; }
    setDopasowywanie({ zrobiono: 0, razem: doDopasowania.length });
    let bledy = 0;
    for (let i = 0; i < doDopasowania.length; i += 1) {
      try {
        await ksefOtrzymaneService.dopasuj(doDopasowania[i].id); // eslint-disable-line no-await-in-loop
      } catch (err) {
        if (err?.isAuthError) break;
        bledy += 1;
      }
      setDopasowywanie({ zrobiono: i + 1, razem: doDopasowania.length });
    }
    setDopasowywanie(null);
    const komunikat = `Dopasowano ponownie ${doDopasowania.length - bledy} z ${doDopasowania.length} faktur${bledy ? ` (błędy: ${bledy})` : ''}`;
    setWynik({ status: bledy ? 'blad' : 'ok', komunikat });
    if (bledy) toast.error(komunikat); else toast.success(komunikat);
    przeladuj();
  };

  // Odpytywanie statusu synchronizacji co 3 s (stan w bazie - dziala po odswiezeniu strony)
  useEffect(() => {
    if (!syncId) return undefined;
    let aktywny = true;
    let timer = null;
    let bledy = 0;
    let odpytan = 0;
    const zakoncz = () => { aktywny = false; setSyncId(null); setPostep(null); };
    const tick = async () => {
      if (!aktywny) return;
      odpytan += 1;
      try {
        const st = await ksefOtrzymaneService.statusSync(syncId);
        if (!aktywny) return;
        if (st?.status === 'ok') {
          zakoncz();
          const komunikat = opisWyniku(st);
          setWynik({ status: 'ok', komunikat });
          toast.success(komunikat);
          await Promise.all([ladujPodsumowanie(), ladujListe()]);
          return;
        }
        if (st?.status === 'blad') {
          zakoncz();
          const komunikat = st.komunikat || 'Pobieranie z KSeF zakończyło się błędem';
          setWynik({ status: 'blad', komunikat });
          toast.error(komunikat);
          await Promise.all([ladujPodsumowanie(), ladujListe()]);
          return;
        }
        bledy = 0;
        setPostep(prev => ({ ...(prev || {}), ...(st || {}), status: 'w_toku' }));
      } catch (err) {
        if (!aktywny) return;
        bledy += 1;
        if (err?.isAuthError || bledy >= MAX_BLEDOW_STATUSU) {
          zakoncz();
          setWynik({ status: 'blad', komunikat: `Nie udało się sprawdzić stanu pobierania (${err?.message || 'błąd'}). Odśwież stronę za chwilę.` });
          return;
        }
      }
      if (odpytan >= MAX_ODPYTAN) {
        zakoncz();
        setWynik({ status: 'blad', komunikat: 'Pobieranie trwa zbyt długo — sprawdź ponownie za chwilę (Przeładuj).' });
        return;
      }
      if (aktywny) timer = setTimeout(tick, ODPYTUJ_CO_MS);
    };
    timer = setTimeout(tick, ODPYTUJ_CO_MS);
    return () => { aktywny = false; if (timer) clearTimeout(timer); };
  }, [syncId, ladujPodsumowanie, ladujListe]);

  const razemStron = Math.max(1, lista.stron || Math.ceil((lista.razem || 0) / NA_STRONE));
  const zajety = uruchamianie || Boolean(syncId) || Boolean(dopasowywanie);
  const ustawienia = podsumowanie?.ustawienia || null;
  const pobieranieWylaczone = Boolean(ustawienia) && ustawienia.pobieranie_zakupow === false;
  const brakTokenu = Boolean(ustawienia) && ustawienia.ma_token === false;
  const ostatnia = podsumowanie?.ostatnia_synchronizacja;
  const odMin = ustawienia?.od_domyslne || OD_CALEJ_HISTORII;

  return (
    <div style={{ fontSize: '12px' }}>
      {podglad && (
        <SzczegolyKsefModal
          id={podglad.id}
          wiersz={podglad}
          sklepy={sklepy}
          onZamknij={() => setPodglad(null)}
          onZmiana={przeladuj}
          onDodano={przeladuj}
        />
      )}

      {/* Naglowek */}
      <div className="card" style={{ marginBottom: '12px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
          <div>
            <h2 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '4px' }}>Faktury z KSeF</h2>
            <p style={{ fontSize: '12px', color: 'var(--text-muted)', margin: 0 }}>
              Faktury zakupu wystawione na nas w Krajowym Systemie e-Faktur — do dodania jako faktury zakupu albo koszty
              {' · '}
              <span data-testid="ostatnia-sync">
                ostatnie pobranie: {ostatnia?.zakonczono ? formatujDateCzas(ostatnia.zakonczono) : (podsumowanie?.sync_w_toku_id ? 'trwa…' : 'nigdy')}
                {ostatnia?.status === 'blad' && ostatnia?.komunikat && <span style={{ color: 'var(--danger)' }}> (błąd: {ostatnia.komunikat})</span>}
                {podsumowanie?.kursor && <span> · pobrane do {podsumowanie.kursor}</span>}
              </span>
            </p>
          </div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
            <button type="button" onClick={przeladuj} disabled={uruchamianie} style={btnSecondary}>Przeładuj</button>
            <button type="button" onClick={pobierz} disabled={zajety} data-testid="btn-pobierz"
              title="Pobierze z KSeF nowe faktury wystawione na nasz NIP (od ostatniej synchronizacji, z zakładką 3 dni wstecz)"
              style={{ ...btnPrimary, opacity: zajety ? 0.6 : 1 }}>
              {syncId ? 'Pobieram z KSeF…' : 'Pobierz z KSeF'}
            </button>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
              <input type="date" aria-label="Pobierz od daty" data-testid="pobierz-od" value={pobierzOd} min={odMin} max={dzisISO()} disabled={zajety}
                onChange={(e) => setPobierzOd(e.target.value)} style={pole} />
              <button type="button" onClick={pobierzOdDaty} disabled={zajety} data-testid="btn-pobierz-od"
                title="Pobierze z KSeF wszystkie faktury od podanej daty (pełne pobranie, potwierdzenie)"
                style={{ ...btnSecondary, border: '1px solid var(--primary)', color: 'var(--primary)', opacity: zajety ? 0.6 : 1 }}>
                Pobierz od…
              </button>
            </span>
            <button type="button" onClick={dopasujPonownie} disabled={zajety || ladowanieListy} data-testid="btn-dopasuj"
              title="Ponownie dopasuje faktury z tej strony do systemu i Kontroli faktur SFD (nowe, dopasowane, błędy)"
              style={{ ...btnSecondary, opacity: zajety ? 0.6 : 1 }}>
              {dopasowywanie ? `Dopasowuję ${dopasowywanie.zrobiono}/${dopasowywanie.razem}…` : 'Dopasuj ponownie'}
            </button>
          </div>
        </div>
        {brakTokenu && (
          <div data-testid="brak-tokenu" style={{ marginTop: '8px', padding: '6px 10px', borderRadius: '4px', fontSize: '11px', backgroundColor: 'var(--danger-light)', color: 'var(--danger)' }}>
            Brak tokenu API KSeF — pobieranie nie zadziała. Wpisz token w zakładce{' '}
            <Link to="/admin?tab=ksef" style={{ color: 'var(--danger)', fontWeight: 600 }}>KSeF</Link>.
          </div>
        )}
        {pobieranieWylaczone && (
          <div data-testid="pobieranie-wylaczone" style={{ marginTop: '8px', padding: '6px 10px', borderRadius: '4px', fontSize: '11px', backgroundColor: 'var(--warning-light)', color: 'var(--warning)' }}>
            Automatyczne pobieranie faktur zakupu z KSeF jest wyłączone — włącz je w zakładce{' '}
            <Link to="/admin?tab=ksef" style={{ color: 'var(--warning)', fontWeight: 600 }}>KSeF</Link>
            {' '}(„Pobieraj faktury zakupu z KSeF”). Ręczne „Pobierz z KSeF” nadal działa, jeśli token jest ustawiony.
          </div>
        )}
        <Postep postep={postep} />
        {wynik && (
          <div data-testid="wynik-sync" style={{
            marginTop: '8px', padding: '6px 10px', borderRadius: '4px', fontSize: '11px',
            backgroundColor: wynik.status === 'ok' ? 'var(--success-light)' : 'var(--danger-light)',
            color: wynik.status === 'ok' ? 'var(--success)' : 'var(--danger)',
          }}>
            {wynik.komunikat}
          </div>
        )}
      </div>

      {blad && (
        <div data-testid="blad" style={{ padding: '8px 12px', backgroundColor: 'var(--danger-light)', color: 'var(--danger)', borderRadius: '4px', marginBottom: '12px', fontSize: '12px' }}>
          {'❌'} {blad}
        </div>
      )}

      {/* Kafelki per status (caly czas) */}
      <div className="card" style={{ marginBottom: '12px' }}>
        {ladowaniePods && !podsumowanie
          ? <div style={{ color: 'var(--text-muted)', padding: '4px 0' }}>Ładowanie podsumowania…</div>
          : <PodsumowanieKsef statusy={podsumowanie?.statusy} razem={podsumowanie?.razem} xmlDoPobrania={podsumowanie?.xml_do_pobrania} aktywnyStatus={status} onStatus={zmienStatus} />}
      </div>

      {/* Per miesiac + sprzedawcy */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '12px', marginBottom: '12px' }}>
        <PerMiesiacKsef miesiace={podsumowanie?.miesiace} />
        <TopSprzedawcy sprzedawcy={podsumowanie?.sprzedawcy} aktywnyNip={nip} onWybierz={wybierzSprzedawce} />
      </div>

      {/* Filtry + tabela */}
      <div className="card" style={{ marginBottom: '12px' }}>
        <div style={{ marginBottom: '10px' }}>
          <FiltryKsef
            status={status} onStatus={zmienStatus} rodzaj={rodzaj} onRodzaj={zmienRodzaj}
            nip={nip} onNip={zmienNip} szukaj={szukaj} onSzukaj={zmienSzukaj}
            zakres={zakres} onZakres={zmienZakres}
            statusy={podsumowanie?.statusy} razem={podsumowanie?.razem} rodzaje={podsumowanie?.rodzaje}
          />
        </div>
        <TabelaKsef wiersze={lista.wiersze} ladowanie={ladowanieListy} sklepy={sklepy} onPodglad={setPodglad} onDodano={przeladuj} onZmiana={przeladuj} />
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '8px', fontSize: '11px', color: 'var(--text-muted)' }}>
          <span data-testid="licznik-wierszy">Razem: {lista.razem} · strona {lista.strona} z {razemStron}</span>
          <div style={{ display: 'flex', gap: '6px' }}>
            <button type="button" disabled={strona <= 1 || ladowanieListy} onClick={() => setStrona(s => Math.max(1, s - 1))} style={{ ...btnSecondary, padding: '4px 10px', fontSize: '11px' }}>‹ Poprzednia</button>
            <button type="button" disabled={strona >= razemStron || ladowanieListy} onClick={() => setStrona(s => s + 1)} style={{ ...btnSecondary, padding: '4px 10px', fontSize: '11px' }}>Następna ›</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default KsefOtrzymanePage;
